import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, 
  Store, Target, Activity, Zap, AlertTriangle, Download 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#9A031E', '#FB8500', '#FFB703', '#023047', '#8ECAE6', '#219EBC'];

export default function RevenueAnalytics() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [customerInsights, setCustomerInsights] = useState(null);
  const [vendorPerformance, setVendorPerformance] = useState(null);
  const [operationalMetrics, setOperationalMetrics] = useState(null);

  useEffect(() => {
    fetchDashboardSummary();
    if (activeTab === 'revenue') {
      fetchRevenueData();
      fetchPredictions();
    } else if (activeTab === 'customers') {
      fetchCustomerInsights();
    } else if (activeTab === 'vendors') {
      fetchVendorPerformance();
    } else if (activeTab === 'operations') {
      fetchOperationalMetrics();
    }
  }, [activeTab, dateRange]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const fetchDashboardSummary = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/analytics/dashboard/summary`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const res = await fetch(
        `${BACKEND_URL}/api/admin/analytics/revenue/overview?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setRevenueData(data);
    } catch (error) {
      toast.error('Failed to fetch revenue data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPredictions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/analytics/revenue/predictions?days_ahead=14`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setPredictions(data);
    } catch (error) {
      console.error('Failed to fetch predictions');
    }
  };

  const fetchCustomerInsights = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const res = await fetch(
        `${BACKEND_URL}/api/admin/analytics/customers/insights?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setCustomerInsights(data);
    } catch (error) {
      toast.error('Failed to fetch customer insights');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorPerformance = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const res = await fetch(
        `${BACKEND_URL}/api/admin/analytics/vendors/performance?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setVendorPerformance(data);
    } catch (error) {
      toast.error('Failed to fetch vendor performance');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationalMetrics = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const res = await fetch(
        `${BACKEND_URL}/api/admin/analytics/operations/metrics?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      setOperationalMetrics(data);
    } catch (error) {
      toast.error('Failed to fetch operational metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const MetricCard = ({ title, value, change, icon: Icon, color = 'text-[hsl(351,96%,31%)]' }) => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-2">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                <span className="text-sm font-medium">{Math.abs(change).toFixed(1)}%</span>
                <span className="text-xs text-muted-foreground">vs last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full bg-[hsl(351,96%,31%,0.1)]`}>
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-8 space-y-6" data-testid="revenue-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" data-font="display">Revenue & Analytics</h1>
          <p className="text-muted-foreground">Intelligent insights and predictive analytics</p>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <MetricCard
            title="Total Revenue"
            value={formatCurrency(summary.revenue?.current)}
            change={summary.revenue?.growth}
            icon={DollarSign}
          />
          <MetricCard
            title="Total Orders"
            value={summary.orders?.current?.toLocaleString()}
            change={summary.orders?.growth}
            icon={ShoppingCart}
          />
          <MetricCard
            title="Active Vendors"
            value={summary.entities?.active_vendors}
            icon={Store}
          />
          <MetricCard
            title="Total Customers"
            value={summary.entities?.total_customers?.toLocaleString()}
            icon={Users}
          />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="revenue">Revenue & Predictions</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[hsl(351,96%,31%)]" />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                {summary && (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Revenue Growth</span>
                      <Badge className={summary.revenue?.growth >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {summary.revenue?.growth >= 0 ? '+' : ''}{summary.revenue?.growth?.toFixed(1)}%
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Order Growth</span>
                      <Badge className={summary.orders?.growth >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {summary.orders?.growth >= 0 ? '+' : ''}{summary.orders?.growth?.toFixed(1)}%
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Active Drivers</span>
                      <span className="font-semibold">{summary.entities?.active_drivers}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  AI Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-green-600 mt-0.5" />
                    <p>Revenue is trending {summary?.revenue?.growth >= 0 ? 'upward' : 'downward'} compared to last period</p>
                  </div>
                  {predictions && (
                    <div className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-blue-600 mt-0.5" />
                      <p>Forecast confidence: <Badge variant="outline" className="ml-1">{predictions.confidence}</Badge></p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* REVENUE TAB */}
        <TabsContent value="revenue" className="space-y-4">
          {loading ? (
            <Card><CardContent className="py-12 text-center">Loading analytics...</CardContent></Card>
          ) : (
            <>
              {/* Revenue Trend Chart */}
              {revenueData?.daily_trend && (
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Trend</CardTitle>
                    <CardDescription>Daily revenue over the selected period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={revenueData.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="_id" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Area type="monotone" dataKey="revenue" stroke="#9A031E" fill="#9A031E" fillOpacity={0.2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Predictions */}
              {predictions && predictions.predictions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-yellow-500" />
                      Revenue Predictions (AI-Powered)
                    </CardTitle>
                    <CardDescription>
                      14-day forecast based on historical trends • Confidence: <Badge variant="outline">{predictions.confidence}</Badge>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Trend</p>
                          <p className="font-semibold capitalize flex items-center gap-1">
                            {predictions.trend === 'increasing' ? <TrendingUp className="h-4 w-4 text-green-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
                            {predictions.trend}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Historical Avg</p>
                          <p className="font-semibold">{formatCurrency(predictions.historical_avg)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Data Points</p>
                          <p className="font-semibold">{predictions.metadata?.days_analyzed} days</p>
                        </div>
                      </div>
                    </div>
                    
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={predictions.predictions}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="predicted_revenue" stroke="#FB8500" strokeWidth={2} name="Predicted" />
                        <Line type="monotone" dataKey="confidence_interval.upper" stroke="#8ECAE6" strokeDasharray="5 5" name="Upper Bound" />
                        <Line type="monotone" dataKey="confidence_interval.lower" stroke="#8ECAE6" strokeDasharray="5 5" name="Lower Bound" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Top Vendors */}
              {revenueData?.top_vendors && (
                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Vendors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={revenueData.top_vendors.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="vendor_name" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="revenue" fill="#9A031E" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* CUSTOMERS TAB */}
        <TabsContent value="customers" className="space-y-4">
          {loading ? (
            <Card><CardContent className="py-12 text-center">Loading customer insights...</CardContent></Card>
          ) : customerInsights && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'New Customers', value: customerInsights.summary?.new_customers },
                            { name: 'Returning Customers', value: customerInsights.summary?.returning_customers }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {[0, 1].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-4 text-center">
                      <p className="text-sm text-muted-foreground">Retention Rate</p>
                      <p className="text-2xl font-bold">{customerInsights.summary?.retention_rate}%</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      Churn Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-yellow-600">{customerInsights.churn_risk?.at_risk_count}</p>
                        <p className="text-sm text-muted-foreground">customers at risk of churning</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Customers with no orders in the last 14 days
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Customers */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Customers by Lifetime Value</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {customerInsights.top_customers?.slice(0, 5).map((customer, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-muted-foreground">{customer.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[hsl(351,96%,31%)]">{formatCurrency(customer.total_spent)}</p>
                          <p className="text-sm text-muted-foreground">{customer.order_count} orders</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* VENDORS TAB */}
        <TabsContent value="vendors" className="space-y-4">
          {loading ? (
            <Card><CardContent className="py-12 text-center">Loading vendor data...</CardContent></Card>
          ) : vendorPerformance && (
            <Card>
              <CardHeader>
                <CardTitle>Vendor Performance Leaderboard</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {vendorPerformance.vendors?.slice(0, 10).map((vendor, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted transition-colors">
                      <div className="w-8 h-8 rounded-full bg-[hsl(351,96%,31%)] text-white flex items-center justify-center font-bold">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{vendor.name}</p>
                        <p className="text-sm text-muted-foreground">{vendor.orders} orders</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{formatCurrency(vendor.revenue)}</p>
                        <p className="text-sm text-muted-foreground">Avg: {formatCurrency(vendor.avg_order_value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* OPERATIONS TAB */}
        <TabsContent value="operations" className="space-y-4">
          {loading ? (
            <Card><CardContent className="py-12 text-center">Loading operational metrics...</CardContent></Card>
          ) : operationalMetrics && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <MetricCard
                  title="Completion Rate"
                  value={`${operationalMetrics.order_completion_rate}%`}
                  icon={Target}
                />
                <MetricCard
                  title="Total Orders"
                  value={operationalMetrics.total_orders?.toLocaleString()}
                  icon={ShoppingCart}
                />
                <MetricCard
                  title="Completed Orders"
                  value={operationalMetrics.completed_orders?.toLocaleString()}
                  icon={Activity}
                />
              </div>

              {operationalMetrics.status_distribution && (
                <Card>
                  <CardHeader>
                    <CardTitle>Order Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={operationalMetrics.status_distribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ _id, count }) => `${_id}: ${count}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {operationalMetrics.status_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {operationalMetrics.peak_hours && (
                <Card>
                  <CardHeader>
                    <CardTitle>Peak Hours Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={operationalMetrics.peak_hours}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="_id" label={{ value: 'Hour (24h)', position: 'insideBottom', offset: -5 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="orders" fill="#023047" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
